// Where a bench's trestles stand: only where the user placed a support (the
// Bench support tool). A bench with none carries no trestle — it is fixed to the
// walls. A support stands across every tier of bench at its place: on a room's
// wall benches, on its rounded corner pieces, on one face of a partition, or on a
// standalone sauna bench's run.
// Pure; the rule each consumer (structure, plan, section, 3D) reads.

import type {
  BenchSupport,
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  isPolyRoom,
  polyBenchCornerBands,
  wallRuns,
} from './polyRoom';

/** A trestle's width along the run (mm). */
export const TRESTLE_WIDTH = 40;
/** The most a row of supports stands apart (mm). */
export const TRESTLE_SPACING = 2700;
/** How far past a bench's end (mm) a wall support still counts as standing on it. */
const COVER_EPS = 1;

/** The stations of a row of supports from `s0` to `s1`: one at each end and as
 *  few between, evenly, as keep them at most {@link TRESTLE_SPACING} apart. One
 *  station when the two coincide. */
export function supportRow(s0: number, s1: number): number[] {
  const L = Math.abs(s1 - s0);
  if (L < 1e-6) return [s0];
  const n = Math.max(1, Math.ceil(L / TRESTLE_SPACING - 1e-9));
  return Array.from({ length: n + 1 }, (_, k) => s0 + ((s1 - s0) * k) / n);
}

/** Where a support's trestle stands on a run whose trestles may sit anywhere in
 *  [lo, hi] (mm): its centre, flush with that end when the support is, else its
 *  station kept half a trestle inside the run. A run narrower than a trestle
 *  takes it in its middle. */
export function trestleCentre(station: number, flush: BenchSupport['flush'], lo: number, hi: number): number {
  const h = TRESTLE_WIDTH / 2;
  if (hi - lo <= TRESTLE_WIDTH) return (lo + hi) / 2;
  if (flush === 'start') return lo + h;
  if (flush === 'end') return hi - h;
  return Math.min(hi - h, Math.max(lo + h, station));
}

/** A support's station (mm) on a run of length `len`: a partition's or a
 *  standalone bench's support stores a fraction of it. */
export function runStation(s: BenchSupport, len: number): number {
  if (s.flush === 'start') return 0;
  if (s.flush === 'end') return len;
  return Math.min(1, Math.max(0, s.at)) * len;
}

/** The supports standing on a wall bench of wall `edge` spanning [start, end]
 *  (mm along the wall, as `polyBenches` stores them). */
export function wallSupportsOn(inst: ProductInstance, edge: number, start: number, end: number): BenchSupport[] {
  return (inst.benchSupports ?? []).filter((s) => s.edge === edge && s.innerWallId == null
    && s.at >= start - COVER_EPS && s.at <= end + COVER_EPS);
}

/** The supports standing on a room's rounded corner pieces at corner `corner`
 *  (numbered by wall). */
export function cornerSupportsOn(inst: ProductInstance, corner: number): BenchSupport[] {
  return (inst.benchSupports ?? []).filter((s) => s.corner === corner);
}

/** The supports standing on a wall bench's rounded end: on any tier of the
 *  stack rounded round the same wall point (`key`), `keyOf` reading a
 *  support's bench end as its stack's key. */
export function roundSupportsOn(inst: ProductInstance, key: string, keyOf: (bench: string, side: 'start' | 'end') => string | undefined): BenchSupport[] {
  return (inst.benchSupports ?? []).filter((s) => s.roundBench != null && keyOf(s.roundBench, s.roundSide ?? 'end') === key);
}

/** The supports standing on the benches of a partition's face. */
export function partitionSupportsOn(inst: ProductInstance, innerWallId: string | undefined, face: 'a' | 'b'): BenchSupport[] {
  if (innerWallId == null) return [];
  return (inst.benchSupports ?? []).filter((s) => s.innerWallId === innerWallId && s.face === face);
}

/** The supports standing on segment `i` of a standalone sauna bench whose
 *  segments are `lens` long, each with its station along that segment (mm). An
 *  older drawing's support (no `seg`) is a fraction of the whole node line, read
 *  onto the first segment holding it. */
export function segSupportsOn(inst: ProductInstance, i: number, lens: number[]): Array<{ s: BenchSupport; at: number }> {
  const total = lens.reduce((a, b) => a + b, 0);
  const S0 = lens.slice(0, i).reduce((a, b) => a + b, 0), L = lens[i] ?? 0;
  const out: Array<{ s: BenchSupport; at: number }> = [];
  for (const s of inst.benchSupports ?? []) {
    if (s.edge != null || s.innerWallId != null || s.corner != null || s.roundBench != null) continue;
    if (s.seg != null) { if (s.seg === i) out.push({ s, at: runStation(s, L) }); continue; }
    const g = runStation(s, total);
    const first = lens.findIndex((l, k) => g <= lens.slice(0, k + 1).reduce((a, b) => a + b, 0) + 1e-6);
    if (first === i) out.push({ s, at: Math.min(L, Math.max(0, g - S0)) });
  }
  return out;
}

/** Whether a flush support makes a tier ending at `start` … `end` (stored, as the
 *  support's own `at`) stand flush with its end: only where that end lies within
 *  `reach` (the tier's depth — what a mitre at a corner moves it) of the support. */
export function flushOnTier(s: BenchSupport, start: number, end: number, reach: number): BenchSupport['flush'] {
  if (s.flush === 'start') return Math.abs(s.at - start) <= reach ? 'start' : undefined;
  if (s.flush === 'end') return Math.abs(s.at - end) <= reach ? 'end' : undefined;
  return undefined;
}

/** The ids of the supports no bench stands on any more: a wall support beyond
 *  every bench of its wall, a partition support whose partition — or that face's
 *  benches — is gone, a corner support whose corner has no rounded piece any more,
 *  a rounded end's whose bench end is no longer rounded,
 *  and any support of a room on a standalone bench's kind of run (or the other
 *  way round). */
export function orphanSupports(inst: ProductInstance): string[] {
  const list = inst.benchSupports ?? [];
  if (!list.length) return [];
  const room = isPolyRoom(inst);
  const runs = room ? wallRuns(inst) : [];
  const pieces = room && list.some((s) => s.corner != null || s.roundBench != null) ? polyBenchCornerBands(inst) : [];
  const corners = new Set(pieces.map((c) => c.vertex));
  const rounds = new Set(pieces.flatMap((c) => (c.roundEnd ? [`${c.roundEnd.bench}:${c.roundEnd.side}`] : [])));
  const covered = (s: BenchSupport): boolean => {
    if (!room) {
      const segs = Math.max(0, (inst.nodes?.length ?? 0) - 1);
      return inst.category === 'saunabench' && s.edge == null && s.innerWallId == null && s.corner == null && s.roundBench == null && (s.seg == null || (s.seg >= 0 && s.seg < segs));
    }
    if (s.roundBench != null) return rounds.has(`${s.roundBench}:${s.roundSide}`);
    if (s.corner != null) return corners.has(s.corner);
    if (s.innerWallId != null) {
      const iw = (inst.innerWalls ?? []).find((w) => w.id === s.innerWallId);
      return !!iw && iw.edge != null && !!s.face && (iw.benches?.[s.face]?.length ?? 0) > 0;
    }
    if (s.edge == null) return false;
    const len = runs[s.edge]?.len;
    if (len == null) return false;
    return (inst.polyBenches ?? []).some((b) => b.edge === s.edge
      && s.at >= Math.min(len, Math.max(0, b.start ?? 0)) - COVER_EPS && s.at <= Math.min(len, Math.max(0, b.end ?? len)) + COVER_EPS);
  };
  return list.filter((s) => !covered(s)).map((s) => s.id);
}

/** A slatted bench run a support stands on, or can be placed on: where it is
 *  stored (`host`), how a point reads as a station along it (mm), and where its
 *  trestles may stand ([lo, hi]). A wall run is measured from the wall's first
 *  corner, the bench there spanning [start, end] (stored); a partition's inward
 *  from the host wall; a rounded corner piece's round its arc; a standalone
 *  bench's segment from its first node — all three a fraction of `len` once
 *  stored. Built from the run itself (`supportRuns`,
 *  benchSolids.ts), never looked up from a point. */
export interface SupportRun {
  kind: 'wall' | 'partition' | 'corner' | 'run';
  host: Pick<BenchSupport, 'edge' | 'innerWallId' | 'face' | 'corner' | 'roundBench' | 'roundSide' | 'seg'>;
  stationOf: (p: Vec2) => number;
  lo: number;
  hi: number;
  len: number;
  start: number;
  end: number;
}

/** The supports a click (`s1` = `s0`) or a drag from `s0` to `s1` on `run`
 *  places: one at each end and as few between as keep them at most
 *  {@link TRESTLE_SPACING} apart (`supportRow`). An end within `tol` (mm) of the
 *  run's own end is placed flush with it. */
export function supportsAlong(run: SupportRun, s0: number, s1: number, tol: number): Array<Omit<BenchSupport, 'id'>> {
  const h = TRESTLE_WIDTH / 2;
  const flushOf = (s: number): BenchSupport['flush'] => (s <= run.lo + tol + h ? 'start' : s >= run.hi - tol - h ? 'end' : undefined);
  const ends = [s0, s1].map((s) => ({ s, flush: flushOf(s) }));
  const at = (e: { s: number; flush: BenchSupport['flush'] }): number => (e.flush === 'start' ? run.lo + h : e.flush === 'end' ? run.hi - h : e.s);
  const single = Math.abs(s1 - s0) < 1e-6;
  const stations = single ? [at(ends[0])] : supportRow(at(ends[0]), at(ends[1]));
  return stations.map((s, k) => {
    const flush = k === 0 ? ends[0].flush : k === stations.length - 1 && !single ? ends[1].flush : undefined;
    if (run.kind === 'wall') {
      const stored = flush === 'start' ? run.start : flush === 'end' ? run.end : Math.min(run.end, Math.max(run.start, s));
      return { ...run.host, at: stored, ...(flush ? { flush } : {}) };
    }
    const t = flush === 'start' ? 0 : flush === 'end' ? 1 : run.len > 0 ? Math.min(1, Math.max(0, s / run.len)) : 0;
    return { ...run.host, at: t, ...(flush ? { flush } : {}) };
  });
}

/** Move a placed support to station `s` (mm) of `run`, onto that run's host (a
 *  standalone bench's segment may change); within `tol` (mm) of the run's end
 *  (−Infinity never) it stands flush with it. */
export function placeSupport(sup: BenchSupport, run: SupportRun, s: number, tol: number): void {
  const [placed] = supportsAlong(run, s, s, tol);
  if (run.kind === 'run') sup.seg = run.host.seg;
  sup.at = placed.at;
  if (placed.flush) sup.flush = placed.flush; else delete sup.flush;
}

/** Where `n` supports stand spread evenly along `run`: the first two flush with
 *  its ends, every one after evenly between — trestle centre to centre the same
 *  (a third in the middle, a fourth makes thirds, …). Alone, one stands flush with
 *  the end nearer station `near`. */
export function evenSupports(run: SupportRun, n: number, near = run.lo): Array<Omit<BenchSupport, 'id'>> {
  if (n <= 0) return [];
  const h = TRESTLE_WIDTH / 2, a = run.lo + h, b = Math.max(a, run.hi - h);
  // The ends taken exactly: a + (b − a)·k / (n − 1) can fall a hair short of b,
  // and the last would no longer read flush.
  const stations = n === 1 ? [Math.abs(near - run.lo) <= Math.abs(run.hi - near) ? a : b] : Array.from({ length: n }, (_, k) => (k === n - 1 ? b : a + ((b - a) * k) / (n - 1)));
  return stations.map((s) => supportsAlong(run, s, s, 1e-6)[0]);
}

/** Take out the supports no bench stands on (`orphanSupports`); true when any went. */
export function pruneBenchSupports(inst: ProductInstance): boolean {
  const gone = new Set(orphanSupports(inst));
  if (!gone.size) return false;
  inst.benchSupports = (inst.benchSupports ?? []).filter((s) => !gone.has(s.id));
  if (!inst.benchSupports.length) delete inst.benchSupports;
  return true;
}

/** Where each wall bench of a room stands (its wall and stored span, mm), keyed
 *  by id: what `followBenches` compares an edit against. */
export type BenchSpans = Map<string, { edge: number; start: number; end: number }>;

export function benchSpans(inst: ProductInstance): BenchSpans {
  const out: BenchSpans = new Map();
  if (!isPolyRoom(inst)) return out;
  const runs = wallRuns(inst);
  for (const b of inst.polyBenches ?? []) {
    const len = runs[b.edge]?.len;
    if (len == null) continue;
    out.set(b.id, { edge: b.edge, start: Math.min(len, Math.max(0, b.start ?? 0)), end: Math.min(len, Math.max(0, b.end ?? len)) });
  }
  return out;
}

/**
 * A wall support follows its bench (the user's rule: nothing is deleted
 * silently). After an edit that left `before` as the room's bench spans, every
 * wall support it made inconsistent — standing past every bench now, or flush
 * with an end no bench of its wall has any more — is put back on the bench that
 * held it: flush with that bench's end when it was, else pulled back to the
 * nearer end of its new span (and flush there, so it keeps following). A support
 * whose bench is gone altogether is left to the orphan prune. Supports an edit
 * kept consistent (a resize, a mirror, an undo) are never touched. True when any
 * moved.
 */
export function followBenches(inst: ProductInstance, before: BenchSpans): boolean {
  if (!inst.benchSupports?.length) return false;
  const now = benchSpans(inst);
  let changed = false;
  for (const s of inst.benchSupports) {
    if (s.edge == null || s.innerWallId != null) continue;
    const onWall = [...now.values()].filter((b) => b.edge === s.edge);
    const covering = onWall.filter((b) => s.at >= b.start - COVER_EPS && s.at <= b.end + COVER_EPS);
    const endOf = (b: { start: number; end: number }): number => (s.flush === 'start' ? b.start : b.end);
    const flushHeld = !s.flush || covering.some((b) => Math.abs(endOf(b) - s.at) <= COVER_EPS);
    if (covering.length && flushHeld) continue;
    // The benches that held it before the edit and still stand on its wall.
    const owners = [...before.entries()]
      .filter(([id, b]) => b.edge === s.edge && s.at >= b.start - COVER_EPS && s.at <= b.end + COVER_EPS && now.get(id)?.edge === s.edge)
      .map(([id, b]) => ({ was: b, is: now.get(id)! }));
    const flushOwner = s.flush ? owners.find((o) => Math.abs(endOf(o.was) - s.at) <= COVER_EPS) : undefined;
    if (flushOwner) {
      s.at = endOf(flushOwner.is);
      changed = true;
      continue;
    }
    if (covering.length) {
      // Still on a bench, but the end it was flush with went: it stays put.
      delete s.flush;
      changed = true;
      continue;
    }
    if (!owners.length) continue;
    const gap = (o: (typeof owners)[number]): number => Math.max(o.is.start - s.at, s.at - o.is.end, 0);
    const o = owners.reduce((a, b) => (gap(b) < gap(a) ? b : a));
    const toStart = Math.abs(s.at - o.is.start) <= Math.abs(s.at - o.is.end);
    s.at = toStart ? o.is.start : o.is.end;
    s.flush = toStart ? 'start' : 'end';
    changed = true;
  }
  return changed;
}
